import * as React from 'react';
import { Link } from 'react-router-dom';
import GoogleBusinessService, {
  GoogleBusinessAccount,
  GoogleBusinessLocation,
  GoogleBusinessConnectedLocation,
} from '../services/GoogleBusinessService';
import { useGoogleBusinessStatus } from '../hooks/useGoogleBusiness';
import env from '../../../../config/env';

const GOOGLE_AUTH_HOST = 'accounts.google.com';
const POLL_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

function getTrustedOrigins(): Set<string> {
  const origins = new Set<string>();
  origins.add(window.location.origin);
  const apiBase = env.API_BASE_URL || env.API_URL;
  if (apiBase) {
    try {
      origins.add(new URL(apiBase).origin);
    } catch { /* ignore invalid URL */ }
  }
  return origins;
}

async function openGooglePopup(getAuthorizeUrl: () => Promise<string | undefined>): Promise<Window> {
  const popup = window.open('about:blank', 'google_oauth', 'width=600,height=700');
  if (!popup) {
    throw new Error('Popup blockiert. Bitte erlauben Sie Popups für diese Seite.');
  }

  const authorize_url = await getAuthorizeUrl();

  if (!authorize_url) {
    popup.close();
    throw new Error('Keine Authorize-URL vom Server erhalten.');
  }

  try {
    const url = new URL(authorize_url);
    if (url.host !== GOOGLE_AUTH_HOST) {
      popup.close();
      throw new Error(`Unerwartete Authorize-Domain: ${url.host}`);
    }
  } catch {
    popup.close();
    throw new Error('Authorize-URL ungültig.');
  }

  popup.location.replace(authorize_url);
  return popup;
}

export function GoogleBusinessIntegration(): React.JSX.Element {
  const { status, isLoading: loading, mutate: mutateStatus, error: statusError } = useGoogleBusinessStatus();
  const [connecting, setConnecting] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [success, setSuccess] = React.useState<string>('');

  const [accounts, setAccounts] = React.useState<GoogleBusinessAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = React.useState<boolean>(false);
  const [selectingAccount, setSelectingAccount] = React.useState<boolean>(false);

  const [locations, setLocations] = React.useState<GoogleBusinessLocation[]>([]);
  const [connectedLocationIds, setConnectedLocationIds] = React.useState<Set<string>>(new Set());
  const [loadingLocations, setLoadingLocations] = React.useState<boolean>(false);
  const [connectingLocationId, setConnectingLocationId] = React.useState<string | null>(null);
  const [disconnectLoading, setDisconnectLoading] = React.useState<boolean>(false);

  const pollRef = React.useRef<number | undefined>(undefined);
  const popupRef = React.useRef<Window | null>(null);
  const trustedOrigins = React.useMemo(() => getTrustedOrigins(), []);

  React.useEffect(() => {
    if (statusError) {
      const statusCode = statusError?.response?.status ?? statusError?.status;
      if (statusCode === 404) {
        // Not configured
      } else if (statusCode === 401) {
        setError('Nicht authentifiziert. Bitte melden Sie sich erneut an.');
      } else if (statusCode === 403) {
        setError('Keine Berechtigung zum Abrufen des Google Business Status.');
      } else {
        const detail = statusError?.response?.data?.detail || statusError?.message || 'Fehler beim Laden des Status';
        setError(detail);
      }
    }
  }, [statusError]);

  // Cleanup polling interval on unmount
  React.useEffect(() => {
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = undefined;
      }
      try {
        popupRef.current?.close();
      } catch { /* ignore */ }
      popupRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    function onMsg(ev: MessageEvent) {
      if (!ev || !ev.data) return;
      if (!trustedOrigins.has(ev.origin)) return;

      if (ev.data === 'GOOGLE_BUSINESS_CONNECTED') {
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = undefined;
        }
        try {
          popupRef.current?.close();
        } catch { /* ignore */ }
        popupRef.current = null;

        setConnecting(false);
        setSuccess('Google Business Profile erfolgreich verbunden!');
        mutateStatus();
      } else if (typeof ev.data === 'object' && ev.data?.type === 'GOOGLE_BUSINESS_OAUTH_ERROR') {
        if (pollRef.current) {
          window.clearInterval(pollRef.current);
          pollRef.current = undefined;
        }
        try {
          popupRef.current?.close();
        } catch { /* ignore */ }
        popupRef.current = null;

        setConnecting(false);
        const reason = ev.data.reason ?? 'unbekannt';
        setError(`Google Login fehlgeschlagen: ${reason}`);
      }
    }

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [mutateStatus, trustedOrigins]);

  const handleConnect = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault();

    setError('');
    setSuccess('');
    setConnecting(true);

    try {
      popupRef.current = await openGooglePopup(async () => {
        const response = await GoogleBusinessService.startConnection();
        return response?.authorize_url;
      });

      // Fallback polling — only checks if popup was closed (postMessage handles the success case)
      const pollStartTime = Date.now();

      pollRef.current = window.setInterval(() => {
        if (Date.now() - pollStartTime > POLL_TIMEOUT_MS) {
          if (pollRef.current) {
            window.clearInterval(pollRef.current);
            pollRef.current = undefined;
          }
          try {
            popupRef.current?.close();
          } catch { /* ignore */ }
          popupRef.current = null;
          setConnecting(false);
          setError('Verbindungs-Timeout. Bitte versuchen Sie es erneut.');
          return;
        }

        // Stop polling if popup was closed without successful connection
        if (popupRef.current?.closed) {
          if (pollRef.current) {
            window.clearInterval(pollRef.current);
            pollRef.current = undefined;
          }
          popupRef.current = null;
          setConnecting(false);
          // One final status check in case postMessage was missed
          mutateStatus();
        }
      }, 2000);
    } catch (err: any) {
      setConnecting(false);
      try {
        popupRef.current?.close();
      } catch { /* ignore */ }
      popupRef.current = null;
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = undefined;
      }
      setError(err?.message ?? 'Unbekannter Fehler beim Starten der Verbindung.');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Google Business Profile Verbindung wirklich trennen?')) return;

    setDisconnectLoading(true);
    setError('');
    setSuccess('');
    try {
      await GoogleBusinessService.disconnect();
      await mutateStatus();
      setAccounts([]);
      setLocations([]);
      setConnectedLocationIds(new Set());
      setSuccess('Google Business Profile Verbindung getrennt');
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Trennen';
      setError(detail);
    } finally {
      setDisconnectLoading(false);
    }
  };

  const handleLoadAccounts = async () => {
    setLoadingAccounts(true);
    setError('');
    try {
      const accountsList = await GoogleBusinessService.getAccounts();
      setAccounts(accountsList);

      if (accountsList.length === 0) {
        setError('Keine Google Business Accounts gefunden. Bitte prüfen Sie, ob Sie Zugriff auf einen Google Business Account haben.');
      } else {
        setSuccess(`${accountsList.length} Account(s) geladen`);
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Laden der Accounts';
      setError(detail);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleSelectAccount = async (accountName: string) => {
    setSelectingAccount(true);
    setError('');
    try {
      await GoogleBusinessService.selectAccount(accountName);
      await mutateStatus();
      setSuccess('Account erfolgreich ausgewählt');
      setAccounts([]);
      handleLoadLocations();
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Auswählen des Accounts';
      setError(detail);
    } finally {
      setSelectingAccount(false);
    }
  };

  const handleLoadLocations = React.useCallback(async () => {
    setLoadingLocations(true);
    setError('');
    try {
      const result = await GoogleBusinessService.getLocations();
      setLocations(result.locations);
      setConnectedLocationIds(new Set(result.connected_location_ids));

      if (result.locations.length === 0) {
        setError('Keine Standorte gefunden. Bitte prüfen Sie, ob der ausgewählte Account Standorte enthält.');
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Laden der Standorte';
      setError(detail);
    } finally {
      setLoadingLocations(false);
    }
  }, []);

  const handleConnectLocation = async (location: GoogleBusinessLocation) => {
    setConnectingLocationId(location.name);
    setError('');
    try {
      const addressLines = location.storefrontAddress?.addressLines || [];
      const city = location.storefrontAddress?.locality || '';
      const postalCode = location.storefrontAddress?.postalCode || '';
      const addressString = [...addressLines, `${postalCode} ${city}`.trim()].filter(Boolean).join(', ');

      await GoogleBusinessService.connectLocation({
        google_location_id: location.name,
        location_name: location.title,
        address: addressString,
      });

      setConnectedLocationIds((prev) => new Set(prev).add(location.name));
      setSuccess(`Standort "${location.title}" verbunden`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Verbinden des Standorts';
      setError(detail);
    } finally {
      setConnectingLocationId(null);
    }
  };

  const handleDisconnectLocation = async (locationName: string, locationTitle: string) => {
    if (!window.confirm(`Standort "${locationTitle}" wirklich trennen?`)) return;

    setConnectingLocationId(locationName);
    setError('');
    try {
      const connectedLocations = await GoogleBusinessService.getConnectedLocations();
      const connectedLocation = connectedLocations.find(
        (loc: GoogleBusinessConnectedLocation) => loc.google_location_id === locationName
      );

      if (connectedLocation) {
        await GoogleBusinessService.disconnectLocation(connectedLocation.id);
      }

      setConnectedLocationIds((prev) => {
        const next = new Set(prev);
        next.delete(locationName);
        return next;
      });
      setSuccess(`Standort "${locationTitle}" getrennt`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.message || 'Fehler beim Trennen des Standorts';
      setError(detail);
    } finally {
      setConnectingLocationId(null);
    }
  };

  React.useEffect(() => {
    if (status?.connected && status?.account_id) {
      handleLoadLocations();
    }
  }, [status?.connected, status?.account_id, handleLoadLocations]);

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <h1 style={{ fontSize: 24, marginBottom: 8, color: '#0f2b5a' }}>Google Business Profile</h1>
      <p style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>
        Verbinden Sie Ihr Google Business Profile um Standorte zu verwalten.
      </p>

      {error && (
        <div
          data-testid="google-business-error"
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#ffebee',
            border: '1px solid #ef5350',
            borderRadius: 6,
            color: '#c62828',
            fontSize: 14,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          data-testid="google-business-success"
          style={{
            padding: 12,
            marginBottom: 16,
            background: '#e8f5e9',
            border: '1px solid #66bb6a',
            borderRadius: 6,
            color: '#2e7d32',
            fontSize: 14,
          }}
        >
          {success}
        </div>
      )}

      {/* Connection Card */}
      <div
        style={{
          background: '#fff',
          border: '1px solid #e0e3ef',
          borderRadius: 8,
          padding: 20,
          marginBottom: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: 0 }}>
            Mit Google Business Profile verbinden
          </h2>
          {loading ? (
            <span style={{ fontSize: 13, color: '#999' }}>Laden...</span>
          ) : status?.connected ? (
            <span
              data-testid="google-business-status-connected"
              style={{
                padding: '4px 12px',
                background: '#e8f5e9',
                color: '#2e7d32',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Verbunden
              {status.connected_at && ` seit ${new Date(status.connected_at).toLocaleDateString('de-DE')}`}
            </span>
          ) : (
            <span
              data-testid="google-business-status-disconnected"
              style={{
                padding: '4px 12px',
                background: '#f5f5f5',
                color: '#666',
                borderRadius: 12,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Nicht verbunden
            </span>
          )}
        </div>

        {!status?.configured && (
          <div
            data-testid="google-business-not-configured"
            style={{
              padding: 12,
              marginBottom: 16,
              background: '#fff3cd',
              border: '1px solid #ffc107',
              borderRadius: 6,
              fontSize: 13,
              color: '#856404',
            }}
          >
            Google Business Integration ist nicht konfiguriert. Bitte kontaktieren Sie den Administrator.
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          {!status?.connected ? (
            <button
              type="button"
              onClick={handleConnect}
              disabled={connecting || !status?.configured}
              data-testid="google-business-connect-button"
              style={{
                padding: '10px 20px',
                background: connecting || !status?.configured ? '#ccc' : '#4285F4',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: connecting || !status?.configured ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {connecting ? 'Verbinde...' : 'Mit Google verbinden'}
            </button>
          ) : (
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnectLoading}
              data-testid="google-business-disconnect-button"
              style={{
                padding: '10px 20px',
                background: '#ef5350',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: disconnectLoading ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              Verbindung trennen
            </button>
          )}
        </div>
      </div>

      {connecting && !status?.connected && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            background: '#e3f2fd',
            border: '1px solid #2196f3',
            borderRadius: 6,
            fontSize: 14,
            color: '#0d47a1',
          }}
        >
          Login im Popup abschliessen...
        </div>
      )}

      {/* Connection Details */}
      {status?.connected && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            background: '#f8f9fa',
            border: '1px solid #e0e3ef',
            borderRadius: 6,
            fontSize: 13,
            color: '#495057',
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 8 }}>Verbindungsdetails:</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
            {status.account_id && (
              <>
                <div>Account:</div>
                <div>{status.account_id}</div>
              </>
            )}
            {status.connected_at && (
              <>
                <div>Verbunden am:</div>
                <div>{new Date(status.connected_at).toLocaleString('de-DE')}</div>
              </>
            )}
            {status.expires_at && (
              <>
                <div>Token läuft ab:</div>
                <div>{new Date(status.expires_at).toLocaleString('de-DE')}</div>
              </>
            )}
            {status.locations_count !== undefined && (
              <>
                <div>Verbundene Standorte:</div>
                <div>{status.locations_count}</div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Account Selection Section */}
      {status?.connected && !status?.account_id && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: 0 }}>Google Business Account</h2>
          </div>

          {accounts.length === 0 ? (
            <button
              type="button"
              onClick={handleLoadAccounts}
              disabled={loadingAccounts}
              data-testid="google-business-load-accounts-button"
              style={{
                padding: '10px 20px',
                background: loadingAccounts ? '#ccc' : '#021a4a',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                cursor: loadingAccounts ? 'not-allowed' : 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {loadingAccounts ? 'Lade Accounts...' : 'Accounts laden'}
            </button>
          ) : (
            <div>
              <p style={{ fontSize: 14, color: '#666', marginBottom: 16 }}>
                Wählen Sie den Account, den Sie mit SundaeOS verbinden möchten:
              </p>
              <table data-testid="google-business-accounts-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e3ef' }}>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>Account Name</th>
                    <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>Typ</th>
                    <th style={{ padding: '8px', textAlign: 'right', fontSize: 13, color: '#666' }}>Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 8px', fontSize: 14 }}>{account.accountName}</td>
                      <td style={{ padding: '12px 8px', fontSize: 14 }}>{account.type || '-'}</td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                        <button
                          type="button"
                          onClick={() => handleSelectAccount(account.name)}
                          disabled={selectingAccount}
                          data-testid={`google-business-select-account-${account.name}`}
                          style={{
                            padding: '6px 12px',
                            background: '#fff',
                            color: '#021a4a',
                            border: '1px solid #021a4a',
                            borderRadius: 4,
                            cursor: selectingAccount ? 'not-allowed' : 'pointer',
                            fontSize: 13,
                            fontWeight: 600,
                          }}
                        >
                          Auswählen
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Locations Section */}
      {status?.connected && status?.account_id && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            padding: 20,
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: 0 }}>Standorte</h2>
            <button
              type="button"
              onClick={handleLoadLocations}
              disabled={loadingLocations}
              data-testid="google-business-refresh-locations-button"
              style={{
                padding: '6px 12px',
                background: 'transparent',
                color: '#021a4a',
                border: '1px solid #021a4a',
                borderRadius: 4,
                cursor: loadingLocations ? 'not-allowed' : 'pointer',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {loadingLocations ? 'Lade...' : 'Aktualisieren'}
            </button>
          </div>

          {loadingLocations ? (
            <p style={{ fontSize: 14, color: '#666' }}>Lade Standorte...</p>
          ) : locations.length === 0 ? (
            <p style={{ fontSize: 14, color: '#666' }}>Keine Standorte gefunden.</p>
          ) : (
            <table data-testid="google-business-locations-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e3ef' }}>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>Name</th>
                  <th style={{ padding: '8px', textAlign: 'left', fontSize: 13, color: '#666' }}>Adresse</th>
                  <th style={{ padding: '8px', textAlign: 'center', fontSize: 13, color: '#666' }}>Status</th>
                  <th style={{ padding: '8px', textAlign: 'right', fontSize: 13, color: '#666' }}>Aktion</th>
                </tr>
              </thead>
              <tbody>
                {locations.map((location) => {
                  const isConnected = connectedLocationIds.has(location.name);
                  const isProcessing = connectingLocationId === location.name;
                  const addressLines = location.storefrontAddress?.addressLines || [];
                  const city = location.storefrontAddress?.locality || '';
                  const postalCode = location.storefrontAddress?.postalCode || '';
                  const addressString = [...addressLines, `${postalCode} ${city}`.trim()].filter(Boolean).join(', ');

                  return (
                    <tr key={location.name} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px 8px', fontSize: 14, fontWeight: 500 }}>{location.title}</td>
                      <td style={{ padding: '12px 8px', fontSize: 14, color: '#666' }}>
                        {addressString || '-'}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'center' }}>
                        {isConnected ? (
                          <span
                            style={{
                              padding: '2px 8px',
                              background: '#e8f5e9',
                              color: '#2e7d32',
                              borderRadius: 8,
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            Verbunden
                          </span>
                        ) : (
                          <span
                            style={{
                              padding: '2px 8px',
                              background: '#f5f5f5',
                              color: '#666',
                              borderRadius: 8,
                              fontSize: 11,
                              fontWeight: 600,
                            }}
                          >
                            Nicht verbunden
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                        {isConnected ? (
                          <button
                            type="button"
                            onClick={() => handleDisconnectLocation(location.name, location.title)}
                            disabled={isProcessing}
                            style={{
                              padding: '6px 12px',
                              background: '#fff',
                              color: '#ef5350',
                              border: '1px solid #ef5350',
                              borderRadius: 4,
                              cursor: isProcessing ? 'not-allowed' : 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {isProcessing ? 'Bitte warten...' : 'Trennen'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => handleConnectLocation(location)}
                            disabled={isProcessing}
                            style={{
                              padding: '6px 12px',
                              background: '#fff',
                              color: '#4285F4',
                              border: '1px solid #4285F4',
                              borderRadius: 4,
                              cursor: isProcessing ? 'not-allowed' : 'pointer',
                              fontSize: 13,
                              fontWeight: 600,
                            }}
                          >
                            {isProcessing ? 'Bitte warten...' : 'Verbinden'}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Reviews Link */}
      {status?.connected && (status?.locations_count ?? 0) > 0 && (
        <div
          style={{
            background: '#fff',
            border: '1px solid #e0e3ef',
            borderRadius: 8,
            padding: 20,
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 600, color: '#0f2b5a', margin: '0 0 4px' }}>
              Google Reviews
            </h2>
            <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
              Bewertungen ansehen und beantworten
            </p>
          </div>
          <Link
            to="/reviews"
            style={{
              padding: '10px 20px',
              background: '#021a4a',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            Reviews ansehen
          </Link>
        </div>
      )}
    </div>
  );
}

export default GoogleBusinessIntegration;
